import {Component, Expressions, Plugins} from '@packages/systems/wfdl';
import {
  EElement,
  EList,
  ERecord,
  EText,
  Expression,
  ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {generateGuid} from '@packages/utilities/guid';
import {type Token} from './parseTextWithTags';

const stringType = qualify(Plugins.name('Basic'), Component.name('String'));
const lineBreakType = qualify(
  Plugins.name('Basic'),
  Component.name('LineBreak')
);

const tokensToText = (tokens: Token[]): string =>
  tokens
    .map((token) => {
      if (token.type === 'text') {
        return token.value;
      }
      if (token.type === 'breakline') {
        return '\n';
      }
      return tokensToText(token.children);
    })
    .join('');

/**
 * Builds element children from parsed tokens.
 * - text tokens: creates new Basic::String elements with new IDs
 * - breakline tokens: creates new Basic::LineBreak elements with new IDs
 * - element tokens: finds the existing element by ID and recursively rebuilds its children;
 *   falls back to a Basic::String carrying the token's own text if the ID is not in the
 *   tree, so an unresolvable inline element loses its markup but never its text
 */
export const buildChildrenFromTokens = (
  tokens: Token[],
  originalElement: ExpressionElement
): Expression[] => {
  return tokens.map((token) => {
    if (token.type === 'text') {
      return EElement({
        id: generateGuid(),
        type: stringType,
        data: EText(token.value),
      });
    } else if (token.type === 'breakline') {
      return EElement({
        id: generateGuid(),
        type: lineBreakType,
        data: ERecord({}),
      });
    } else {
      const existingElement = Expressions.getElementById(
        EElement(originalElement),
        token.id
      );

      if (!existingElement) {
        return EElement({
          id: generateGuid(),
          type: stringType,
          data: EText(tokensToText(token.children)),
        });
      }

      if (token.children && token.children.length > 0) {
        const newChildren = buildChildrenFromTokens(
          token.children,
          originalElement
        );

        const updatedElement = Expressions.setElementData(
          existingElement,
          Expressions.sparseSetIn(
            Expressions.getElementData(existingElement),
            [{in: 'Record', at: 'children'}],
            EList(newChildren)
          )
        );

        return EElement(updatedElement);
      }

      return EElement(existingElement);
    }
  });
};
